import React, { useEffect, useState, useRef } from 'react';
import { api, API_BASE, logHttpError } from '../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import TrophyShelf from '../components/TrophyShelf';
import SettingsModal from '../components/SettingsModal';
import MediaViewer from '../components/MediaViewer';
import type { MediaItem } from '../components/MediaViewer';
import ReactionPicker from '../components/ReactionPicker';
import type {
  AcquireSecurityOperation,
  CompleteCurrentSessionRevoke,
  ReleaseSecurityOperation,
  SecurityOperation,
} from '../hooks/useSecurityOperationMutex';
import type {
  AcknowledgeRecoveryCodes,
  PendingRecoveryCodes,
  PublishRecoveryCodes,
} from '../components/settings/recoveryCodeMemory';

interface UserData {
  id: number;
  username: string;
  full_name: string | null;
  email: string | null;
  birth_date: string | null;
  clan_id: number;
  mood_score: number;
  avatar_url: string | null;
  banner_url: string | null;
  is_admin: boolean;
}

interface UserStats {
  posts_count: number;
  likes_count: number;
}

interface WallMessage {
  id: number;
  author_username: string;
  author_avatar_url: string | null;
  author_id?: number;
  content: string;
  file_url: string | null;
  file_type: string | null;
  message_type: string;
  created_at: string;
}

interface TrophyOption {
  id: number;
  name: string;
  description: string;
}

const CLAN_COLORS: Record<number, { name: string; color: string }> = {
  1: { name: 'Акула', color: '#00d2ff' },
  2: { name: 'Слон', color: '#ff9966' },
  3: { name: 'Горилла', color: '#71B280' },
  4: { name: 'Волк', color: '#a8a9ad' },
};

type MessageType = 'praise' | 'reprimand' | 'neutral';

// Популярные эмодзи
const QUICK_EMOJIS = ['😀', '😂', '🥰', '😎', '🤔', '👍', '❤️', '', '🎉', '', '💬', '✅'];

// Компонент мини-профиля для стены
const WallMiniProfile: React.FC<{ userId: number; onClose: () => void }> = ({ userId, onClose }) => {
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState({ posts_count: 0, likes_count: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await api.get(`/auth/user/${userId}`);
        setUser(userRes.data);
        const statsRes = await api.get(`/profile/stats/${userId}`);
        setStats(statsRes.data);
      } catch (err) {
        logHttpError("Ошибка загрузки профиля", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userId, onClose]);

  if (loading || !user) return null;

  const avatarSrc = user.avatar_url
    ? `${API_BASE}${user.avatar_url}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=random&color=fff`;

  const clanNames: Record<number, string> = {
    1: 'Акула 🦈',
    2: 'Слон 🐘',
    3: 'Горилла 🦍',
    4: 'Волк 🐺'
  };

  return (
    <div ref={popupRef} className="absolute top-full left-0 mt-2 w-80 bg-[#1a1d24] border border-gray-700 rounded-3xl shadow-2xl z-[9999] overflow-hidden animate-fade-in">
      {user?.banner_url ? (
        <div className="relative h-24">
          <img src={`${API_BASE}${user.banner_url}`} alt="Banner" className="w-full h-24 object-cover" />
          <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full w-8 h-8 flex items-center justify-center transition">✕</button>
        </div>
      ) : (
        <div className="h-24 bg-gradient-to-r from-blue-600 to-emerald-600 relative flex items-center justify-center">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full w-8 h-8 flex items-center justify-center transition">✕</button>
        </div>
      )}

      <div className="relative px-6 -mt-14 mb-4 flex justify-center">
        <img src={avatarSrc} alt={user.username} className="w-28 h-28 rounded-full border-4 border-[#1a1d24] object-cover bg-[#0f1115] shadow-xl" />
      </div>

      <div className="px-6 pb-6 text-center">
        <h3 className="text-2xl font-black text-white mb-1">{user.username}</h3>
        {user.full_name && <p className="text-sm text-gray-400 mb-4 font-medium">{user.full_name}</p>}

        <div className="grid grid-cols-3 gap-2 mb-6 bg-[#0f1115]/50 p-3 rounded-2xl border border-gray-800">
          <div className="text-center">
            <div className="text-lg font-bold text-white">{stats.posts_count}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Постов</div>
          </div>
          <div className="text-center border-x border-gray-800">
            <div className="text-lg font-bold text-white">{stats.likes_count}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Лайков</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-emerald-400">{user.mood_score}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Рейтинг</div>
          </div>
        </div>

        <div className="space-y-2 mb-6 text-left bg-[#0f1115]/30 p-4 rounded-xl">
           {user.email && <p className="text-xs text-gray-400 flex items-center gap-2"><span>📧</span> {user.email}</p>}
           <p className="text-xs text-gray-400 flex items-center gap-2"><span>🛡️</span> Клан: <span className="text-white font-bold">{clanNames[user.clan_id] || 'Без клана'}</span></p>
           {user.is_admin && <p className="text-xs text-red-400 flex items-center gap-2 font-bold"><span>👑</span> Администратор проекта</p>}
        </div>

        <button onClick={() => { navigate(`/profile/${user.id}`); onClose(); }} className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20">
          👤 Открыть полный профиль
        </button>
      </div>
    </div>
  );
};

interface ProfilePageProps {
  onLogout?: () => void;
  securityOperation: SecurityOperation | null;
  acquireSecurityOperation: AcquireSecurityOperation;
  releaseSecurityOperation: ReleaseSecurityOperation;
  onCurrentSessionRevoked: CompleteCurrentSessionRevoke;
  pendingRecoveryCodes: PendingRecoveryCodes | null;
  publishRecoveryCodes: PublishRecoveryCodes;
  acknowledgeRecoveryCodes: AcknowledgeRecoveryCodes;
}

const ProfilePage: React.FC<ProfilePageProps> = ({
  onLogout = () => undefined,
  securityOperation,
  acquireSecurityOperation,
  releaseSecurityOperation,
  onCurrentSessionRevoked,
  pendingRecoveryCodes,
  publishRecoveryCodes,
  acknowledgeRecoveryCodes,
}) => {
  const { userId: urlUserId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const currentUserId = localStorage.getItem('sharkly_user_id');
  const isAdmin = localStorage.getItem('sharkly_is_admin') === 'true';

  const isOwnProfile = !urlUserId || urlUserId === currentUserId;
  const targetUserId = urlUserId || currentUserId;

  const profileCardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [user, setUser] = useState<UserData | null>(null);
  const [stats, setStats] = useState<UserStats>({ posts_count: 0, likes_count: 0 });
  const [wallMessages, setWallMessages] = useState<WallMessage[]>([]);
  const [wallText, setWallText] = useState('');
  const [messageType, setMessageType] = useState<MessageType>('neutral');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [availableTrophies, setAvailableTrophies] = useState<TrophyOption[]>([]);
  const [selectedTrophyId, setSelectedTrophyId] = useState<string>('');
  const [trophyReason, setTrophyReason] = useState('');
  const [awardingLoading, setAwardingLoading] = useState(false);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMedia, setViewerMedia] = useState<MediaItem[]>([]);

  const [privacy, setPrivacy] = useState({
    hide_wall: false, hide_avatar: false, hide_full_name: false,
    hide_birth_date: false, hide_email: false
  });

  useEffect(() => {
    if (user && profileCardRef.current) {
      profileCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [targetUserId]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!targetUserId) {
        setLoading(false);
        setError("Нет ID пользователя");
        return;
      }
      try {
        const res = await api.get(`/auth/user/${targetUserId}`);
        setUser(res.data);
        fetchStats(res.data.id);
        fetchWallMessages(res.data.id);
        // Загружаем настройки приватности
        try {
          const privRes = await api.get(`/profile/privacy/${targetUserId}`);
          setPrivacy(privRes.data);
        } catch {}
      } catch (err: any) {
        logHttpError("[Profile] Ошибка загрузки профиля", err);
        setError(err.response?.data?.detail || `Ошибка ${err.response?.status || 'соединения'}`);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [targetUserId, currentUserId]);

  useEffect(() => {
    if (isAdmin && showAdminPanel && availableTrophies.length === 0) {
      api.get('/trophies/list')
        .then(res => setAvailableTrophies(res.data))
        .catch(err => logHttpError("Ошибка загрузки трофеев", err));
    }
  }, [isAdmin, showAdminPanel]);

  const fetchStats = async (targetUserId: number) => {
    try {
      const res = await api.get(`/profile/stats/${targetUserId}`);
      setStats(res.data);
    } catch (err) {
      logHttpError("Ошибка загрузки статистики", err);
    }
  };

  const fetchWallMessages = async (targetUserId: number) => {
    try {
      const res = await api.get(`/profile/${targetUserId}/wall`);
      setWallMessages(res.data);
    } catch (err) {
      logHttpError("Ошибка загрузки стены", err);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploadingAvatar(true);
      const res = await api.post('/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUser(prev => prev ? { ...prev, avatar_url: res.data.avatar_url } : null);
    } catch (err) {
      logHttpError("Ошибка загрузки аватара", err);
      alert("Не удалось загрузить аватар");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewFile(file);
    }
  };

  const insertEmoji = (emoji: string) => {
    setWallText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handlePostToWall = async () => {
    if (!wallText.trim() && !previewFile) return;
    if (!user) return;

    try {
      let fileUrl = null;
      let fileType = null;

      if (previewFile) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', previewFile);

        const uploadRes = await api.post('/profile/wall/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        fileUrl = uploadRes.data.file_url;
        fileType = uploadRes.data.file_type;
        setUploadingFile(false);
      }

      await api.post('/profile/wall', {
        content: wallText,
        to_user_id: user.id,
        message_type: messageType,
        file_url: fileUrl,
        file_type: fileType
      });

      setWallText('');
      setPreviewFile(null);
      setMessageType('neutral');
      fetchWallMessages(user.id);
      fetchStats(user.id);
    } catch (err: any) {
      logHttpError("Ошибка отправки на стену", err);
      alert(err.response?.data?.detail || "Не удалось отправить сообщение");
      setUploadingFile(false);
    }
  };

  const [editingWall, setEditingWall] = useState<number | null>(null);
  const [editWallText, setEditWallText] = useState('');

  const handleDeleteWall = async (msgId: number) => {
    if (!confirm('Удалить сообщение?')) return;
    try {
      await api.delete(`/profile/wall/${msgId}`);
      if (user) fetchWallMessages(user.id);
    } catch {}
  };

  const handleEditWall = async (msgId: number) => {
    if (!editWallText.trim()) return;
    try {
      await api.put(`/profile/wall/${msgId}`, { content: editWallText });
      setEditingWall(null);
      if (user) fetchWallMessages(user.id);
    } catch {}
  };

  const handleAwardTrophy = async () => {
    if (!selectedTrophyId || !user) return;
    setAwardingLoading(true);
    try {
      await api.post(`/trophies/award?target_user_id=${user.id}`, {
        trophy_id: Number(selectedTrophyId),
        reason: trophyReason || undefined
      });
      alert('Трофей успешно выдан!');
      setShowAdminPanel(false);
      setSelectedTrophyId('');
      setTrophyReason('');
      window.location.reload();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Ошибка при выдаче трофея");
    } finally {
      setAwardingLoading(false);
    }
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case 'praise': return '👍';
      case 'reprimand': return '';
      default: return '';
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'praise': return 'text-green-400 bg-green-500/10 border-green-500/30';
      case 'reprimand': return 'text-red-400 bg-red-500/10 border-red-500/30';
      default: return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
    }
  };

  const getMessageTypeLabel = (type: string) => {
    switch (type) {
      case 'praise': return 'Похвала';
      case 'reprimand': return 'Выговор';
      default: return 'Сообщение';
    }
  };

  const getAvatarUrl = (username: string, avatarUrl: string | null, size: number = 40) => {
    if (avatarUrl) return `${API_BASE}${avatarUrl}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&color=fff&size=${size}`;
  };

  // Рендер медиа в сообщении на стене
  const renderWallMedia = (msg: WallMessage) => {
    if (!msg.file_url) return null;

    const url = `${API_BASE}${msg.file_url}`;

    if (msg.file_type === 'image') {
      return (
        <div className="mt-3 w-fit max-w-full mx-auto rounded-xl overflow-hidden border border-gray-700">
          <img
            src={url}
            alt="Attachment"
            className="block max-w-full h-auto object-contain cursor-pointer rounded-xl"
            onClick={() => {
              setViewerMedia([{
                type: 'image' as const,
                url
              }]);
              setViewerOpen(true);
            }}
          />
        </div>
      );
    }
    if (msg.file_type === 'video') {
      return (
        <div className="mt-3 rounded-xl overflow-hidden border border-gray-700 bg-black">
          <video src={url} controls className="w-full max-h-[500px]" />
        </div>
      );
    }
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-3 p-3 bg-gray-800 rounded-xl hover:bg-gray-700 transition border border-gray-700">
        <span className="text-2xl">📄</span>
        <span className="text-sm font-medium">Скачать файл</span>
      </a>
    );
  };

  const handleNameClick = (msgId: number) => {
    setActiveMessageId(activeMessageId === msgId ? null : msgId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1115] text-white">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0f1115] text-gray-400 p-4 text-center">
        <h2 className="text-2xl font-bold mb-2 text-red-400">Профиль не загружен</h2>
        <p className="mb-4 text-sm bg-red-500/10 px-4 py-2 rounded-lg border border-red-500/20">{error || "Пользователь не найден"}</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-500 transition">Попробовать снова</button>
      </div>
    );
  }

  const clan = CLAN_COLORS[user.clan_id] || { name: 'Без клана', color: '#666' };
  const avatarSrc = (user.avatar_url && (!privacy.hide_avatar || isOwnProfile)) ? `${API_BASE}${user.avatar_url}` : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=${clan.color.replace('#', '')}&color=fff`;

  return (
    <div className="max-w-5xl mx-auto p-6 pb-24 md:pb-6 animate-fade-in relative">
      {/* Карточка профиля */}
      <div ref={profileCardRef} className="bg-[#1a1d24] rounded-3xl border border-gray-800 relative overflow-hidden shadow-2xl scroll-mt-6">
        {user.banner_url ? (
          <img src={`${API_BASE}${user.banner_url}`} alt="Banner" className="absolute top-0 left-0 right-0 w-full h-40 object-cover" />
        ) : (
          <div className="absolute top-0 left-0 right-0 h-40 opacity-20" style={{ background: `linear-gradient(to bottom, ${clan.color}, transparent)` }} />
        )}

        {isOwnProfile && (
          <button onClick={() => setIsSettingsOpen(true)} className="absolute top-6 right-6 z-20 p-3 rounded-full bg-gray-800/80 hover:bg-gray-700 text-gray-400 hover:text-white transition border border-gray-700 backdrop-blur-sm group" title="Настройки">
            <span className="text-xl group-hover:rotate-90 transition-transform duration-300 block">⚙️</span>
          </button>
        )}

        <div className="relative z-10 p-8 flex flex-col md:flex-row items-center md:items-start gap-8">
          <div className="relative group">
            <img
              src={avatarSrc}
              alt="Avatar"
              className="w-36 h-36 rounded-full object-cover border-4 shadow-lg bg-[#0f1115] cursor-pointer"
              style={{ borderColor: clan.color }}
              onClick={() => {
                if (user?.avatar_url) {
                  setViewerMedia([{
                    type: 'image' as const,
                    url: `${API_BASE}${user.avatar_url}`
                  }]);
                  setViewerOpen(true);
                }
              }}
            />
            {isOwnProfile && (
              <div
                className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  avatarInputRef.current?.click();
                }}
              >
                <span className="text-white font-bold text-sm text-center px-2">{uploadingAvatar ? 'Загрузка...' : 'Сменить фото'}</span>
              </div>
            )}
            {isOwnProfile && <input type="file" ref={avatarInputRef} className="hidden" accept="image/png,image/jpeg,image/gif,image/webp" onChange={handleAvatarUpload} disabled={uploadingAvatar} />}
          </div>

          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl font-black text-white mb-2 tracking-tight flex items-center gap-3 flex-wrap justify-center md:justify-start">
              {user.username}
              {user.is_admin && <span className="bg-red-500/20 text-red-400 text-sm px-3 py-1 rounded-full border border-red-500/30 font-bold uppercase flex items-center gap-1">👑 Админ</span>}
            </h1>
            {(!privacy.hide_full_name || isOwnProfile) && user.full_name && <p className="text-xl text-gray-300 mb-6 font-medium">{user.full_name}</p>}
            <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
              <span className="px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide border" style={{ backgroundColor: `${clan.color}15`, borderColor: `${clan.color}40`, color: clan.color }}>{clan.name}</span>
              <span className="px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm font-bold uppercase tracking-wide">⚡ Mood: {user.mood_score}</span>
            </div>
            <div className="text-sm text-gray-500 space-y-1 bg-[#15171c] p-4 rounded-xl inline-block">
              {(!privacy.hide_email || isOwnProfile) && user.email && <p>📧 {user.email}</p>}
              {(!privacy.hide_birth_date || isOwnProfile) && user.birth_date && <p>🎂 {new Date(user.birth_date).toLocaleDateString('ru-RU')}</p>}
              {!isOwnProfile && privacy.hide_email && privacy.hide_birth_date && <p className="text-gray-600">Информация скрыта настройками приватности</p>}
            </div>
            {!isOwnProfile && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <button onClick={() => navigate(`/messages?user=${user.id}`)} className="px-6 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold transition shadow-[0_0_15px_rgba(37,99,235,0.4)]">💬 Написать</button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 divide-x divide-gray-800 border-t border-gray-800 bg-[#15171c]">
          <div className="p-6 text-center hover:bg-gray-800/30 transition"><div className="text-3xl font-black text-white mb-1">{stats.posts_count}</div><div className="text-xs text-gray-500 uppercase tracking-widest font-bold">постов</div></div>
          <div className="p-6 text-center hover:bg-gray-800/30 transition"><div className="text-3xl font-black text-white mb-1">{stats.likes_count}</div><div className="text-xs text-gray-500 uppercase tracking-widest font-bold">лайков</div></div>
          <div className="p-6 text-center hover:bg-gray-800/30 transition"><div className="text-3xl font-black text-white mb-1">{user.mood_score}</div><div className="text-xs text-gray-500 uppercase tracking-widest font-bold">рейтинг</div></div>
        </div>
      </div>

      {/* Полка с трофеями */}
      <div className="mt-10">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">🏆 Полка наград</h2>
        <TrophyShelf userId={user.id} />
        {isAdmin && (
          <div className="mt-6 bg-[#1a1d24] border border-red-500/30 rounded-2xl p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full blur-3xl -mr-10 -mt-10"></div>
            <div className="relative z-10">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">Панель администратора: Выдача наград</h3>
                <button onClick={() => setShowAdminPanel(!showAdminPanel)} className="text-xs bg-gray-800 hover:bg-gray-700 px-3 py-1 rounded-lg transition">{showAdminPanel ? 'Скрыть' : 'Открыть'}</button>
              </div>
              {showAdminPanel && (
                <div className="space-y-4 animate-fade-in">
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Выберите трофей</label>
                    <select value={selectedTrophyId} onChange={(e) => setSelectedTrophyId(e.target.value)} className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none appearance-none cursor-pointer">
                      <option value="">-- Выберите награду --</option>
                      {availableTrophies.map(t => (<option key={t.id} value={t.id}>{t.name}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-2">Причина выдачи (необязательно)</label>
                    <input type="text" value={trophyReason} onChange={(e) => setTrophyReason(e.target.value)} placeholder="За что выдается награда?" className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none" />
                  </div>
                  <button onClick={handleAwardTrophy} disabled={!selectedTrophyId || awardingLoading} className={`w-full py-3 rounded-xl font-bold transition ${selectedTrophyId && !awardingLoading ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white shadow-[0_0_20px_rgba(220,38,38,0.4)]' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`}>
                    {awardingLoading ? 'Выдача...' : '🏆 Выдать трофей'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Стена профиля */}
      {(!privacy.hide_wall || isOwnProfile) ? (
      <div className="mt-10">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3"> Стена профиля</h2>

        {/* Форма отправки с медиа и эмодзи */}
        <div className="bg-[#1a1d24] p-4 rounded-2xl border border-gray-800 mb-6">
          <textarea
            value={wallText}
            onChange={(e) => setWallText(e.target.value)}
            placeholder={isOwnProfile ? "Напиши что-нибудь себе на стену..." : `Напиши что-нибудь ${user.username}...`}
            className="w-full bg-[#15171c] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none resize-none min-h-[80px] mb-3"
          />

          {/* Предпросмотр файла */}
          {previewFile && (
            <div className="mb-3 p-3 bg-gray-900/50 rounded-xl border border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {previewFile.type.startsWith('image/') ? (
                  <img src={URL.createObjectURL(previewFile)} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                ) : previewFile.type.startsWith('video/') ? (
                  <span className="text-3xl">🎬</span>
                ) : (
                  <span className="text-3xl">📄</span>
                )}
                <div>
                  <p className="text-sm font-bold text-white">{previewFile.name}</p>
                  <p className="text-xs text-gray-500">{(previewFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button onClick={() => setPreviewFile(null)} className="text-gray-400 hover:text-red-400 p-2">✕</button>
            </div>
          )}

          {/* Панель эмодзи */}
          {showEmojiPicker && (
            <div className="mb-3 p-3 bg-gray-900 rounded-xl border border-gray-700 grid grid-cols-6 gap-2 animate-fade-in">
              {QUICK_EMOJIS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => insertEmoji(emoji)}
                  className="text-2xl hover:bg-gray-700 rounded-lg p-2 transition"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}

          {/* Выбор типа сообщения */}
          {!isOwnProfile && (
            <div className="flex flex-wrap gap-2 mb-3">
              <button onClick={() => setMessageType('praise')} className={`px-4 py-2 rounded-full text-sm font-bold transition border ${messageType === 'praise' ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}>👍 Похвала (+рейтинг)</button>
              <button onClick={() => setMessageType('reprimand')} className={`px-4 py-2 rounded-full text-sm font-bold transition border ${messageType === 'reprimand' ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}>👎 Выговор (-рейтинг)</button>
              <button onClick={() => setMessageType('neutral')} className={`px-4 py-2 rounded-full text-sm font-bold transition border ${messageType === 'neutral' ? 'bg-blue-500/20 border-blue-500/50 text-blue-400' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600'}`}>📝 Просто сообщение</button>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`p-3 rounded-full transition ${showEmojiPicker ? 'bg-yellow-500/20 text-yellow-400' : 'hover:bg-gray-700 text-gray-400'}`}
                title="Эмодзи"
              >
                😀
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="p-3 rounded-full hover:bg-gray-700 text-gray-400 transition"
                title="Прикрепить файл"
              >
                {uploadingFile ? '⏳' : '📎'}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/*,.pdf,.doc,.docx,.zip"
                onChange={handleFileSelect}
                disabled={uploadingFile}
              />
            </div>

            <button
              onClick={handlePostToWall}
              disabled={(!wallText.trim() && !previewFile) || uploadingFile}
              className={`px-6 py-2 rounded-full font-bold transition ${
                (wallText.trim() || previewFile) && !uploadingFile
                  ? isOwnProfile
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                    : messageType === 'praise'
                    ? 'bg-green-600 hover:bg-green-500 text-white shadow-[0_0_15px_rgba(34,197,94,0.4)]'
                    : messageType === 'reprimand'
                    ? 'bg-red-600 hover:bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.4)]'
                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {uploadingFile ? 'Загрузка...' : (isOwnProfile ? '📝 Отправить' : messageType === 'praise' ? '👍 Похвалить' : messageType === 'reprimand' ? '👎 Выговорить' : '📝 Отправить')}
            </button>
          </div>
        </div>

        {/* Список сообщений */}
        <div className="space-y-4">
          {wallMessages.length === 0 ? (
            <div className="text-center py-10 bg-[#1a1d24] rounded-2xl border border-gray-800 border-dashed"><p className="text-gray-500">На стене пока пусто... Будь первым!</p></div>
          ) : (
            wallMessages.map((msg) => {
              const hasAuthorId = msg.author_id && msg.author_id > 0;
              const isPopupActive = activeMessageId === msg.id;

              return (
                <div
                  key={msg.id}
                  className={`bg-[#1a1d24] p-4 rounded-2xl border border-gray-800 hover:border-gray-700 transition ${isPopupActive ? 'relative z-[100]' : 'relative z-0'}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {hasAuthorId ? (
                      <>
                        <img
                          src={getAvatarUrl(msg.author_username, msg.author_avatar_url, 32)}
                          alt={msg.author_username}
                          className="w-8 h-8 rounded-full object-cover border border-gray-700 shrink-0 cursor-pointer hover:ring-2 hover:ring-blue-500 transition"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMessageId(isPopupActive ? null : msg.id);
                          }}
                        />
                        <span
                          className="font-bold text-sm text-gray-300 cursor-pointer hover:text-blue-400 transition"
                          onClick={() => handleNameClick(msg.id)}
                        >
                          {msg.author_username}
                        </span>
                      </>
                    ) : (
                      <>
                        <img src={getAvatarUrl(msg.author_username, msg.author_avatar_url, 32)} alt={msg.author_username} className="w-8 h-8 rounded-full object-cover border border-gray-700 shrink-0" />
                        <span className="font-bold text-sm text-gray-300">{msg.author_username}</span>
                      </>
                    )}

                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${getMessageTypeColor(msg.message_type)}`}>
                      {getMessageTypeIcon(msg.message_type)} {getMessageTypeLabel(msg.message_type)}
                    </span>
                    <span className="text-xs text-gray-600">{new Date(msg.created_at).toLocaleString('ru-RU')}</span>
                    {msg.author_id?.toString() === currentUserId && (
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => { setEditingWall(msg.id); setEditWallText(msg.content); }} className="text-gray-600 hover:text-yellow-400 text-xs transition">✏️</button>
                        <button onClick={() => handleDeleteWall(msg.id)} className="text-gray-600 hover:text-red-400 text-xs transition">🗑️</button>
                      </div>
                    )}
                  </div>

                  {editingWall === msg.id ? (
                    <div className="pl-11 mb-2">
                      <textarea value={editWallText} onChange={e => setEditWallText(e.target.value)}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg p-2 text-white text-sm resize-none focus:border-blue-500 outline-none" rows={2} />
                      <div className="flex gap-2 mt-1">
                        <button onClick={() => handleEditWall(msg.id)} className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg">Сохранить</button>
                        <button onClick={() => setEditingWall(null)} className="text-xs px-3 py-1 text-gray-400 hover:text-white">Отмена</button>
                      </div>
                    </div>
                  ) : msg.content && <p className="text-gray-400 text-sm pl-11 mb-2">{msg.content}</p>}

                  {/* Рендер медиа */}
                  {renderWallMedia(msg)}

                  {/* Реакции */}
                  <div className="mt-2">
                    <ReactionPicker entityType="wall" entityId={msg.id} />
                  </div>

                  {/* Мини-профиль */}
                  {isPopupActive && hasAuthorId && (
                    <div className="absolute top-full left-11 mt-2 z-[9999]">
                      <WallMiniProfile userId={msg.author_id!} onClose={() => setActiveMessageId(null)} />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
      ) : (
        <div className="mt-10 text-center py-12 bg-[#1a1d24] rounded-2xl border border-gray-800 border-dashed">
          <p className="text-3xl mb-2">🔒</p>
          <p className="text-gray-500">Стена скрыта настройками приватности</p>
        </div>
      )}

      {/* Достижения */}
      <div className="mt-10 mb-10">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">🏅 Достижения</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {['Первый пост', 'Любитель стай', 'Мастер дискуссий', 'Ночной боец', 'Популярный'].map((name, i) => (
            <div key={i} className="bg-[#1a1d24] p-5 rounded-2xl border border-gray-800 flex flex-col items-center text-center opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all duration-300">
              <div className="text-4xl mb-2">🔒</div>
              <div className="text-sm font-bold text-gray-300">{name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Модальное окно настроек */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onLogout={onLogout}
        securityOperation={securityOperation}
        acquireSecurityOperation={acquireSecurityOperation}
        releaseSecurityOperation={releaseSecurityOperation}
        onCurrentSessionRevoked={onCurrentSessionRevoked}
        pendingRecoveryCodes={pendingRecoveryCodes}
        publishRecoveryCodes={publishRecoveryCodes}
        acknowledgeRecoveryCodes={acknowledgeRecoveryCodes}
      />

      {/* Media Viewer */}
      <MediaViewer isOpen={viewerOpen} onClose={() => setViewerOpen(false)} media={viewerMedia} />
    </div>
  );
};

export default ProfilePage;
